import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import _ from "lodash";

import {createSelector} from "reselect";

import {geoUtils} from '../utils/geo-utils';


/**
 * Component for drawing shapes to handle mouse interactions on the County map.
 *
 * Allows transparent shapes to be drawn above the visible map to handle hover
 * and click interactions and styling based on hover and selection.
 *
 * @property height - height of the parent svg in pixels for placement
 * @property width - width of the parent svg in pixels for placement
 * @property stateFips - numeric state fips value to retrieve state county shapes
 * @property selectedIds - array of county fips ids to style as selected
 * @property onClick - callback when a county is clicked (countyFips,stateFips)
 * @property onAltClick - callback when a county is alt clicked (countyFips,stateFips)
 * @property onHoverOver - callback when a county is moused over (countyFips,cent)
 * @property onHoverOut - callback when a county is moused out (countyFips)
 *
 * @example
 * return (
 *   <StateCountyHovers
 *     width={500}
 *     height={500}
 *     stateFips={1}
 *     selectedIds={[...]}
 *     onClick={(countyFips, stateFips) => {}}
 *     onAltClick={(countyFips, stateFips) => {}}
 *     onHoverOver={(countyFips, stateFips) => {}}
 *     onHoverOut={(countyFips) => {}}
 *   />
 * )
 */
export default class StateCountyHovers extends PureComponent {

  static propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    selectedIds: PropTypes.array,
    stateFips: PropTypes.number,
    onHoverOver: PropTypes.func,
    onHoverOut: PropTypes.func,
    onClick: PropTypes.func,
    onAltClick: PropTypes.func
  }

  getFips = (props) => props.stateFips
  getWidth = (props) => props.width
  getHeight = (props) => props.height

  getPaths = createSelector(
    [this.getFips, this.getWidth, this.getHeight],
    (fips, width, height) => geoUtils.getMercatorStateCountyPaths(fips, width, height)
  )

  render() {
    const paths = this.getPaths(this.props);

    const getHandlers = county => {
      const onClick = this.props.onClick;
      const onAltClick = this.props.onAltClick;
      const onHoverOver = this.props.onHoverOver;
      const onHoverOut = this.props.onHoverOut;
      const handlers = {};

      if (onClick) {
        handlers.onClick = (event) => {
          if (onAltClick && (event.metaKey || event.ctrlKey)) {
            onAltClick(county.countyFips, county.stateFips);
          }
          else {
            onClick(county.countyFips, county.stateFips);
          }
        }
      }

      if (onHoverOver) {
        handlers.onMouseEnter = () => onHoverOver(county.countyFips, county.cent);

        if (onHoverOut) {
          handlers.onMouseOut = () => onHoverOut(county.countyFips);
        }
        else {
          handlers.onMouseOut = () => onHoverOver(null);
        }
      }

      return handlers;
    };

    const getClassName = (id) => {
      return _.includes(this.props.selectedIds, id) ? "selected" : ""
    }

    return (
      <g className="StateCountyHovers">
        {paths.map(d => {
          return (
            <path
              key={d.id}
              fillOpacity={0}
              className={getClassName(d.id)}
              d={d.path}
              {...getHandlers(d)}
            />
          )
        })}
      </g>
    )
  }
}
